import React, { useState } from 'react';
import './MediaTab.css';

const MediaTab = ({ posts, profile, onSetProfilePicture, isLoading = false }) => {
  // Gather all profile pictures from avatar_history (if available), fallback to avatar_url
  const avatarHistory = Array.isArray(profile?.avatar_history)
    ? profile.avatar_history
    : profile?.avatar_url
      ? [profile.avatar_url]
      : [];

  const profilePictures = avatarHistory.map((url, idx) => ({
    id: `profile-avatar-${idx}`,
    media_url: url,
    media_type: 'image',
    isProfileAvatar: profile.avatar_url === url,
  }));

  // Gather all post media (images/videos)
  const postMedia = posts
    .filter(post => post.media_url && (post.media_type === 'image' || post.media_type === 'video'))
    .map(post => ({
      ...post,
      isProfileAvatar: false,
    }));

  // Combine for modal navigation
  const allMedia = [...profilePictures, ...postMedia];

  const [modalIndex, setModalIndex] = useState(null);

  const openModal = (idx) => setModalIndex(idx);
  const closeModal = () => setModalIndex(null);

  const showPrev = () => setModalIndex(idx => (idx > 0 ? idx - 1 : allMedia.length - 1));
  const showNext = () => setModalIndex(idx => (idx < allMedia.length - 1 ? idx + 1 : 0));

  const handleSetProfilePicture = () => {
    if (onSetProfilePicture && allMedia[modalIndex].media_type === 'image') {
      onSetProfilePicture(allMedia[modalIndex].media_url);
      closeModal();
    }
  };

  const isPreparing = isLoading && (!posts || posts.length === 0) && (!profile || !profile.avatar_url);

  return (
    <div className="profile-section">
      <h2>My Media</h2>

      {isPreparing && (
        <div className="media-skeleton">
          <div className="skeleton-card shimmer" style={{ height: 140, borderRadius: 12 }} />
          <div className="skeleton-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 10 }}>
            <div className="skeleton-card shimmer" style={{ height: 90, borderRadius: 10 }} />
            <div className="skeleton-card shimmer" style={{ height: 90, borderRadius: 10 }} />
            <div className="skeleton-card shimmer" style={{ height: 90, borderRadius: 10 }} />
          </div>
        </div>
      )}

      {/* Profile Pictures */}
      {profilePictures.length > 0 && (
        <div>
          <h3 className="media-category-title">Profile Pictures</h3>
          <div className="media-grid">
            {profilePictures.map((pic, idx) => (
              <div
                className="media-item profile-avatar-thumb"
                key={pic.id}
                onClick={() => openModal(idx)}
              >
                <img src={pic.media_url} alt="Profile avatar" />
                {pic.isProfileAvatar && (
                  <span className="profile-avatar-badge">Current</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Post Media */}
      <h3 className="media-category-title">Post Media</h3>
      <div className="media-grid">
        {postMedia.length > 0 ? (
          postMedia.map((post, idx) => (
            <div
              className="media-item post-media-thumb"
              key={post.id}
              onClick={() => openModal(profilePictures.length + idx)}
            >
              {post.media_type === 'image' ? (
                <img src={post.media_url} alt="Post media" />
              ) : (
                <video src={post.media_url} />
              )}
            </div>
          ))
        ) : (
          <p>No media uploaded yet.</p>
        )}
      </div>

      {/* Modal for previewing media */}
      {modalIndex !== null && (
        <div className="media-modal-overlay" onClick={closeModal}>
          <div className="media-modal-content" onClick={e => e.stopPropagation()}>
            <button className="media-modal-close" onClick={closeModal}>&times;</button>
            <button className="media-modal-prev" onClick={showPrev}>&#8592;</button>
            <div className="media-modal-media">
              {allMedia[modalIndex].media_type === 'image' ? (
                <img src={allMedia[modalIndex].media_url} alt="Media preview" />
              ) : (
                <video src={allMedia[modalIndex].media_url} controls autoPlay />
              )}
            </div>
            <button className="media-modal-next" onClick={showNext}>&#8594;</button>
            {allMedia[modalIndex].media_type === 'image' &&
              !allMedia[modalIndex].isProfileAvatar && (
                <button
                  className="set-profile-picture-btn"
                  onClick={handleSetProfilePicture}
                >
                  Set as Profile Picture
                </button>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaTab;